/**
 * 共用音效管理（告警鈴聲／點餐通知音效）
 *
 * 規則（詳見 CONTEXT.md「音效通知」）：
 * - 告警鈴聲優先級高於點餐通知音效，可隨時中斷點餐通知音效，被中斷的不補播
 * - 告警鈴聲播放期間到達的點餐事件只留一個待播標記，告警結束後補播一輪
 * - 點餐通知音效一輪為 3 次、每次間隔 0.5 秒，一輪進行中到達的事件併入本輪
 * - 只有主聲分頁（最後 focus 的分頁）會發聲
 */
import level3SirenSrc from '@/assets/audio/level3-siren.mp3'
import level4SirenSrc from '@/assets/audio/level4-siren.mp3'
import orderNotifySrc from '@/assets/audio/orderfood-notify.mp3'

const SIREN_DURATION = 6000 // 告警鈴聲播放長度（毫秒）
const ORDER_NOTIFY_REPEAT = 3 // 點餐通知音效每輪播放次數
const ORDER_NOTIFY_GAP = 500 // 點餐通知音效每次播放間隔（毫秒）
const ORDER_NOTIFY_MAX_DURATION = 3000 // 單次播放上限，避免未觸發 ended 時卡住（毫秒）

export const SOUND_TYPE = {
  SIREN: 'siren',
  ORDER_NOTIFY: 'orderNotify'
}

let sirenAudio = null
let sirenLevel = null
let sirenTimerId = null

let orderAudio = null
let isOrderPlaying = false
let orderGapTimerId = null
let orderGuardTimerId = null
let hasPendingOrderNotify = false

let errorHandler = null

/**
 * 設定播放失敗時的處理函式
 * @param {Function} handler - 接收 { type, error }，type 為 SOUND_TYPE 其中之一
 */
export const setErrorHandler = (handler) => {
  errorHandler = handler
}

/**
 * 當前分頁是否為主聲分頁（最後 focus 的分頁）
 * @returns {boolean}
 */
export const isLastFocusTab = () => {
  return sessionStorage.getItem('isLastFocus') === 'true'
}

/**
 * 取得正在播放的告警等級
 * @returns {number|null} 未播放時為 null
 */
export const getSirenLevel = () => sirenLevel

/**
 * 取得目前播放狀態（唯讀，供除錯與後門頁使用）
 * @returns {{ sirenLevel: number|null, isOrderPlaying: boolean, hasPendingOrderNotify: boolean, isLastFocusTab: boolean }}
 */
export const getState = () => ({
  sirenLevel,
  isOrderPlaying,
  hasPendingOrderNotify,
  isLastFocusTab: isLastFocusTab()
})

/**
 * 播放音效，失敗時交給 errorHandler
 * @param {HTMLAudioElement} audio
 * @param {string} type - SOUND_TYPE
 * @param {Function} [onFail] - 播放失敗後的清理
 */
const playAudio = (audio, type, onFail) => {
  const playPromise = audio.play()
  if (playPromise === undefined) return
  playPromise.catch((error) => {
    if (onFail) onFail()
    if (errorHandler) errorHandler({ type, error })
  })
}

/**
 * 停止告警鈴聲，不處理待播
 */
const clearSiren = () => {
  if (sirenAudio) {
    sirenAudio.pause()
    sirenAudio.currentTime = 0
    sirenAudio = null
  }
  if (sirenTimerId) {
    clearTimeout(sirenTimerId)
    sirenTimerId = null
  }
  sirenLevel = null
}

/**
 * 停止點餐通知音效，不留待播
 */
const clearOrderNotify = () => {
  if (orderAudio) {
    orderAudio.pause()
    orderAudio.currentTime = 0
    orderAudio = null
  }
  if (orderGapTimerId) {
    clearTimeout(orderGapTimerId)
    orderGapTimerId = null
  }
  if (orderGuardTimerId) {
    clearTimeout(orderGuardTimerId)
    orderGuardTimerId = null
  }
  isOrderPlaying = false
}

/**
 * 播放點餐通知音效的第 playCount + 1 次
 * @param {number} playCount - 本輪已播放次數
 */
const playOrderNotifyOnce = (playCount) => {
  orderGapTimerId = null
  orderAudio = new Audio(orderNotifySrc)
  orderAudio.volume = 1.0
  const audio = orderAudio
  let isFinished = false
  const onFinish = () => {
    // 被中斷或已結束則不再往下播
    if (isFinished || orderAudio !== audio) return
    isFinished = true
    clearTimeout(orderGuardTimerId)
    orderGuardTimerId = null
    orderAudio = null
    const nextCount = playCount + 1
    if (nextCount >= ORDER_NOTIFY_REPEAT) {
      isOrderPlaying = false
      return
    }
    orderGapTimerId = setTimeout(() => playOrderNotifyOnce(nextCount), ORDER_NOTIFY_GAP)
  }
  audio.addEventListener('ended', onFinish, { once: true })
  orderGuardTimerId = setTimeout(onFinish, ORDER_NOTIFY_MAX_DURATION)
  // 播放失敗時結束本輪，不重試
  playAudio(audio, SOUND_TYPE.ORDER_NOTIFY, () => {
    if (orderAudio === audio) clearOrderNotify()
  })
}

/**
 * 開始一輪點餐通知音效
 */
const startOrderNotifyRound = () => {
  isOrderPlaying = true
  playOrderNotifyOnce(0)
}

/**
 * 播放告警鈴聲，會中斷點餐通知音效與正在播放的告警鈴聲
 * @param {number} level - 告警等級（3 或 4）
 */
export const playSiren = (level) => {
  clearOrderNotify()
  clearSiren()
  sirenAudio = new Audio(level === 3 ? level3SirenSrc : level4SirenSrc)
  // 設定音量為最大值
  sirenAudio.volume = 1.0
  sirenLevel = level
  // 設定 6 秒後停止播放
  sirenTimerId = setTimeout(stopSiren, SIREN_DURATION)
  const audio = sirenAudio
  playAudio(audio, SOUND_TYPE.SIREN, () => {
    if (sirenAudio === audio) stopSiren()
  })
}

/**
 * 停止告警鈴聲，若有待播的點餐通知音效且仍為主聲分頁則補播一輪
 */
export const stopSiren = () => {
  clearSiren()
  if (!hasPendingOrderNotify) return
  hasPendingOrderNotify = false
  if (isLastFocusTab()) startOrderNotifyRound()
}

/**
 * 觸發點餐通知音效
 * - 非主聲分頁：不播放
 * - 告警鈴聲播放中：留下待播，告警結束後補播一輪
 * - 點餐通知音效播放中：併入本輪
 */
export const playOrderNotify = () => {
  if (!isLastFocusTab()) return
  if (sirenLevel !== null) {
    hasPendingOrderNotify = true
    return
  }
  if (isOrderPlaying) return
  startOrderNotifyRound()
}

/**
 * 停止所有音效並清除待播
 */
export const stopAll = () => {
  hasPendingOrderNotify = false
  clearSiren()
  clearOrderNotify()
}
